"use client"

import { Button } from "@mui/material"
import { Links, ShopLinks } from "common/SiteLinks"
import useDebounce from "common/bit/hooks/useDebounce"
import { useScrollDirection } from "common/bit/hooks/useScrollDirection"
import CookieBanner from "components/CookieBanner/CookieBanner"
import AuthDialog from "components/shopFeatures/auth/AuthDialog"
import { NavLinksProvider } from "contexts/NavLinksContext"
import { usePathname } from "next/navigation"
import { FC, useEffect, useRef, useState } from "react"
import styled from "styled-components"
import Footer from "./footer/Footer"
import Header from "./header/Header"
import { mapMenuToNavLinks } from "./header/mapMenuToNavLinks"
export { HEADER_HEIGHT_D, HEADER_HEIGHT_M } from "theme/shared"

const StyledMain = styled.main`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  flex-grow: 1;

  :focus {
    outline: 0;
  }
`

const StyledButton = styled(Button)`
  position: fixed;
  z-index: -10;
  background-color: black;
  color: white;
  top: 0;
  &:focus-visible {
    z-index: 9999999;
  }
`

const Layout: FC<any> = ({ children, footerData, menuData }) => {
  const mainRef = useRef<HTMLDivElement>(null)
  const navLinks = mapMenuToNavLinks(menuData)
  const pathname = usePathname()
  const [narrowHeader, setNarrowHeader] = useState(false)

  const scrollDirection = useScrollDirection()
  const debouncedScrollDirection = useDebounce(scrollDirection, 100)

  useEffect(() => {
    if (debouncedScrollDirection === "down") {
      setNarrowHeader(true)
    } else {
      setNarrowHeader(false)
    }
  }, [debouncedScrollDirection])

  // ✅ Dynamic model page — matches /model/123/cx5 or similar
  const isModelPage = /^\/model\/[^/]+\/[^/]+$/.test(pathname)

  // ✅ Dynamic showroom screen — matches /showroomsscreens/something
  const isShowroomScreen = /^\/showroomsscreens\/[^/]+$/.test(pathname)

  // ✅ Dynamic landing pages — matches /landing/slug
  const isLandingPage = /^\/landing\/[^/]+$/.test(pathname)

  const getHeader = () => {
    // ✅ SHOP pages (excluding main online purchase)
    if (
      pathname.indexOf("/shop") > -1 &&
      pathname !== Links.OnlinePurchase.link
    ) {
      return (
        <Header
          narrow={true}
          links={ShopLinks}
          hamburgerLink="/shop"
          isShopHeader={true}
        />
      )
    }

    // ✅ If not any of those special routes → show regular header
    if (
      !isModelPage &&
      pathname !== "/about-us" &&
      !isShowroomScreen &&
      !isLandingPage
    ) {
      return <Header narrow={narrowHeader} links={navLinks} />
    }

    // ✅ Otherwise hide header
    return false
  }

  const handleClick = () => {
    if (mainRef.current) {
      mainRef.current.focus()
    }
  }

  return (
    <NavLinksProvider navLinks={navLinks}>
      <div id="__next">
        <StyledButton id="skip-to-content" onClick={handleClick} tabIndex={2}>
          דלג לתוכן המרכזי
        </StyledButton>
        {getHeader()}

        <StyledMain id="mainContent" tabIndex={-1} ref={mainRef} role="main">
          {children}
        </StyledMain>
        {!pathname.startsWith("/shop") &&
          !isShowroomScreen &&
          !isLandingPage && <Footer footerData={footerData} />}
        <CookieBanner />
        <AuthDialog />
      </div>
    </NavLinksProvider>
  )
}

export default Layout
